import { PROGRESS_LABEL_MAX_LENGTH } from './shared-responder-v2-core-label.consts';
import { STEP_LABEL_SOURCES } from './shared-responder-v2-core-session.consts';
import type {
  ResponderSessionState,
  ResponderStepLabel,
} from './shared-responder-v2-core-session.types';

/**
 * Resolves the text shown for one step in the progress list and the
 * required-questions modal — both render this same object, so the tooltip
 * (`fullText`, shown only when `source` is `truncated`) is data-driven rather
 * than a prop either surface has to compute itself.
 */
export function resolveStepLabel(args: {
  aiShortTitle: string | null;
  questionText: string;
  maxLength?: number;
}): ResponderStepLabel {
  const maxLength = args.maxLength ?? PROGRESS_LABEL_MAX_LENGTH;
  const trimmedShortTitle = args.aiShortTitle?.trim() ?? '';

  if (trimmedShortTitle !== '') {
    return {
      source: STEP_LABEL_SOURCES.AiShortTitle,
      text: trimmedShortTitle,
      fullText: args.questionText,
    };
  }

  if (args.questionText.length <= maxLength) {
    return {
      source: STEP_LABEL_SOURCES.Full,
      text: args.questionText,
      fullText: args.questionText,
    };
  }

  return {
    source: STEP_LABEL_SOURCES.Truncated,
    text: `${args.questionText.slice(0, maxLength - 1).trimEnd()}…`,
    fullText: args.questionText,
  };
}

/**
 * Applies AI short titles to the steps that have one, leaving the rest on their
 * truncated fallback. Lives beside `resolveStepLabel` because it is the only
 * caller that turns a resolved title back into a step label.
 */
export function applyStepLabels(
  state: ResponderSessionState,
  labels: readonly { readonly questionId: string; readonly title: string }[]
): ResponderSessionState {
  const byId = new Map(labels.map((entry) => [entry.questionId, entry.title]));

  return {
    ...state,
    steps: state.steps.map((step) => {
      const title = byId.get(step.questionId);

      if (title === undefined) {
        return step;
      }

      return {
        ...step,
        label: resolveStepLabel({
          aiShortTitle: title,
          questionText: step.label.fullText,
        }),
      };
    }),
  };
}
